import React, { useState, useEffect, Fragment,useRef} from "react";
import { useTheme } from "../../ThemeContext";
import { Dialog, Transition } from "@headlessui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import {toast} from "react-toastify"
import {
  getAllTeams,
  addTeam,
  getTeamDetails,
  allMembers,
  deleteTeam,
  getMember,
  allocatedMembers,
  deleteMember,
} from "../../api/api";

const Teams = ({ project_id }) => {
  const { theme } = useTheme();

  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetail, setTeamDetails] = useState(null);
  const [member, setMember] = useState([]);
  const [teamMembers, setAllTeamMembers] = useState([]);

  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [isCreatingDefault, setIsCreatingDefault] = useState(false);

  // ✅ Fetch Teams (NO infinite loop)
  const creatingDefaultRef = useRef(false); // To prevent multiple default team creation
  const fetchTeams = async () => {
  try {
    if (creatingDefaultRef.current) return;

    const response = await getAllTeams({ projectId: project_id });
    let teamList = response?.data?.teamName || [];

    // 🔹 If no teams at all → create Default Team
    if (teamList.length === 0 && !creatingDefaultRef.current) {
      creatingDefaultRef.current = true;

      await addTeam({
        projectId: project_id,
        teamName: "Default Team",
      });

      const newResponse = await getAllTeams({ projectId: project_id });
      teamList = newResponse?.data?.teamName || [];

      creatingDefaultRef.current = false;
    }

    setTeams(teamList);

    // 🔹 Auto select first team (default will be first if only one)
    if (!selectedTeam?._id && teamList.length > 0) {
      handleTeamClick(teamList[0]._id);
    }
  } catch (error) {
    console.error("Error fetching teams:", error);
    creatingDefaultRef.current = false;
  }
};

  useEffect(() => {
    fetchTeams();
  }, [project_id]); // ✅ only project_id

  // ✅ Fetch all project members
  const fetchMembers = async () => {
    try {
      const response = await allMembers({ projectId: project_id });
      setAllTeamMembers(response.data.teams || []);
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, [project_id]);

  // ✅ Fetch team details
  const handleTeamClick = async (teamId) => {
    try {
      const res = await getTeamDetails({ teamId }); // ✅ correct key

      const details = res.data.teamDetails;

      setTeamDetails(details);
      setMember(details.allocatedMember || []);
      setSelectedTeam(details);
    } catch (error) {
      console.error("Error fetching team details:", error);
    }
  };

  // ✅ Open user modal
  const handleUserModalOpen = async (team) => {
    try {
      const response = await getMember({ projectId: project_id });
      setUsers(response.data.members || []);
      setSelectedTeam(team);
      setIsUserModalOpen(true);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  // ✅ Select users
  const handleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // ✅ Allocate members
  const handleAllocateMembers = async () => {
    if (!teamDetail?._id || selectedUsers.length === 0) return;

    try {
      await allocatedMembers({
        teamId: teamDetail._id,
        allocatedMember: selectedUsers,
      });

      const res = await getTeamDetails({ teamId: teamDetail._id });
      const details = res.data.teamDetails;

      setTeamDetails(details);
      setMember(details.allocatedMember || []);
      setSelectedTeam(details);

      setSelectedUsers([]);
      setIsUserModalOpen(false);
      toast.success("Member allocated successfully" )
    } catch (error) {
      console.error("Error allocating members:", error);
    }
  };

  // ✅ Add team
  const handleAddTeam = async () => {
    if (!newTeamName.trim()) return;

    try {
      await addTeam({ projectId: project_id, teamName: newTeamName });

      const updatedTeams = await getAllTeams({ projectId: project_id });
      setTeams(updatedTeams.data.teamName || []);

      setNewTeamName("");
      setIsTeamModalOpen(false);
      toast.success("Team created successfully");
    } catch (error) {
      console.error("Error adding new team:", error);
    }
  };

  // ✅ Delete team
  const handleDeleteTeam = async (teamId) => {
    try {
      await deleteTeam({ teamId });
      setTeams((prev) => prev.filter((team) => team._id !== teamId));

      if (selectedTeam?._id === teamId) {
        setSelectedTeam(null);
        setTeamDetails(null);
        setMember([]);
      }
      toast.success("Team deleted successfully");
    } catch (error) {
      console.error("Error deleting team:", error);
    }
  };

  // ✅ Delete member
  const handleDeleteMember = async (userId) => {
    try {
      await deleteMember({
        teamId: selectedTeam._id,
        memberIds: [userId],
      });

      const res = await getTeamDetails({ teamId: selectedTeam._id });
      const details = res.data.teamDetails;

      setTeamDetails(details);
      setMember(details.allocatedMember || []);
      setSelectedTeam(details);
      toast.success("Member removed successfully");
    } catch (error) {
      console.error("Error deleting member:", error);
    }
  };


    return (
        <div
            className={`flex h-screen gap-2 ${theme === "dark" ? "bg-gray-900 text-gray-100" : "bg-gray-100 text-gray-800"}`}
        >
            {/* Sidebar */}
            <div className={`md:w-1/5 p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg mr-4 w-1/4`}>
                <div className='flex justify-between gap-10 items-center'>
                    <h2 className="text-2xl font-bold mb-6 cursor-pointer">Teams</h2>
                    <button
                        className=" mb-4 text-white"
                        onClick={() => setIsTeamModalOpen(true)} // Open the team modal
                    >
                        <FaPlus className="h-8 w-8 rounded-full bg-green-500 p-2" />
                    </button>
                </div>
                {teams.length > 0 ? (
                    <div className="space-y-4">
                        {teams.map((team) => (
                            <div
                                key={team._id}
                                className={`p-3 rounded-lg shadow flex justify-between items-center transition ${selectedTeam?._id === team._id
                                    ? "bg-blue-500 text-white"
                                    : "bg-gray-700 hover:bg-gray-800"
                                    }`}
                            >
                                <span
                                    className="cursor-pointer font-medium"
                                    onClick={() => handleTeamClick(team._id)}
                                >
                                    {team.teamName}
                                </span>
                               {
                               <button 
                            disabled={team.teamName === "Default Team"}
                               onClick={() => 
                               handleDeleteTeam(team._id)}>
                                <FaTrash className="h-4 w-4 rounded-full bg-red-500 text-white p-1" />
                            </button>
                               }

                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500">No teams found.</p>
                )}
            </div>

            {/* Main Content */}
            <div className={`md:w-4/5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'} rounded-md p-5 w-3/4 `}>
                {selectedTeam ? (
                    <>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold mb-4">{teamDetail.teamName}</h2>
                            <div className="flex items-center">
                                <button
                                    className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 mr-2"
                                    onClick={() => {
                                        setSelectedTeam(teamDetail._id);
                                        handleUserModalOpen(teamDetail._id);
                                    }}
                                >
                                    <FaPlus />
                                </button>
                            </div>
                        </div>
                        {selectedTeam && member.length > 0 ? (
                            <div className="space-y-4">
                                {member.map((member) => (
                                    <div key={member._id} className="flex justify-between items-center">
                                        <span>{member.name}</span>
                                        <button
                                            className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                                            onClick={() => handleDeleteMember(member._id)}
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500">No Members  In Team</p>
                        )}

                    </>
                ) : (
                    <div>
                        <p className="text-2xl mb-5 text-center"> All Members</p>
                        <div className='grid grid-cols-2 gap-5 '>
                        {teamMembers.map((member, index) => (
                            <div key={member._id} className="flex justify-between items-center">
                                 
                                <span>{member.name}</span>
                                <button
                                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                                    onClick={() => handleDeleteMember(member._id)}
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        ))}
                        </div>
                    </div>

                )}
            </div>

            {/* Add Team Modal */}
            <Transition show={isTeamModalOpen} as={Fragment}>
                <Dialog onClose={() => setIsTeamModalOpen(false)} className="relative z-10">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className={`w-full max-w-md p-6 rounded-lg shadow-lg ${theme === "dark" ? "bg-gray-800 text-gray-100" : "bg-white"}`}
                            >
                                <Dialog.Title className="text-lg font-bold mb-4">Add New Team</Dialog.Title>
                                <input
                                    type="text"
                                    placeholder="Enter team name"
                                    value={newTeamName}
                                    onChange={(e) => setNewTeamName(e.target.value)}
                                    className={`bg-gray-50 border border-gray-300 text-sm rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full p-2.5 ${theme === "dark"
                                        ? "bg-gray-700 text-white border-gray-600"
                                        : ""
                                        }`}
                                />
                                <div className="px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse justify-between">

                                    <button
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
                                        onClick={handleAddTeam} // Add the new team
                                    >
                                        Add Team
                                    </button>
                                    <button
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"

                                        onClick={() => setIsTeamModalOpen(false)} // Close the modal
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>

            {/* User Modal */}
            <Transition show={isUserModalOpen} as={Fragment}>
                <Dialog onClose={() => setIsUserModalOpen(false)} className="relative z-10">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>
                    <div className="fixed inset-0 flex items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className={`w-full max-w-md p-6 rounded-lg shadow-lg ${theme === "dark" ? "bg-gray-800 text-gray-100" : "bg-white"}`}
                            >
                                <Dialog.Title className="text-lg font-bold mb-4">Add Members</Dialog.Title>
                                {/* Make the list of users scrollable */}
                                <div className="space-y-4 max-h-80 overflow-y-auto">
                                    {users.map((user) => (
                                        <div key={user._id} className="flex justify-items-center m-5">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user._id)}
                                                className="mr-4 form-checkbox h-4 w-4 text-indigo-600"
                                                onChange={() => handleUserSelection(user._id)}
                                            />
                                            <label>{user.name}</label>
                                        </div>
                                    ))}
                                </div>
                                <div className="px-4 py-3 mt-4 sm:px-6 sm:flex sm:flex-row-reverse justify-between">
                                    <button
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto"
                                        onClick={handleAllocateMembers}
                                    >
                                        Allocate
                                    </button>
                                    <button
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
                                        onClick={() => setIsUserModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>

        </div>
    );
};

export default Teams;
